import { createContext, useState, useEffect, useRef, useMemo, useCallback } from "react";
import { auth, db } from '../firebase';
import {
    collection,
    getDocs,
    doc,
    getDoc,
    updateDoc,
    setDoc,
    Timestamp,
    onSnapshot
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { v4 as uuid } from 'uuid';
import { rarityXpMultiplier, getMaxLevel, getXpForLevel, getAscensionCost } from "../utilities";
import { getLeagueForTrophies } from "../config/arenaLeagues";

export const UserContext = createContext(null);

// ------------------------------------------------------------------------------------
//
// Helpers to keep Firestore updates valid (no undefined) and limited to stored shape.
//
// ------------------------------------------------------------------------------------
export function sanitizeForFirestore(value) {
    if (Array.isArray(value)) return value.map(sanitizeForFirestore);
    if (value && typeof value === 'object') {
        const out = {};
        for (const [k, v] of Object.entries(value)) {
            if (v === undefined) continue;
            out[k] = sanitizeForFirestore(v);
        }
        return out;
    }
    return value;
}

// Only keep the instance fields we actually store in Firestore
export function stripToInstanceShape(ownedCards) {
    return (ownedCards || []).map(c => {
        const obj = {
            instanceId: c.instanceId,
            cardId: c.cardId,
            level: Number(c.level ?? 1),
            xp: Number(c.xp ?? 0),
            // Ensure evolution is 1-based everywhere we persist
            evolutionStage: Math.max(1, Number(c.evolutionStage ?? 1)),
            active: !!c.active,
            defenseActive: !!c.defenseActive,
        };

        if (typeof c.activeOrder === 'number') {
            obj.activeOrder = Number(c.activeOrder);
        }
        if (typeof c.defenseOrder === 'number') {
            obj.defenseOrder = Number(c.defenseOrder);
        }

        if (typeof c.ascensions === 'number') obj.ascensions = Number(c.ascensions);
        if (typeof c.evolutionBonus === 'number') obj.evolutionBonus = c.evolutionBonus;

        return obj;
    });
}

// Local-date key used for daily rewards (YYYY-MM-DD, local time)
function getTodayKey() {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

// Local quarter id used for league rewards + resets (e.g. "2025-Q4", local time)
function getCurrentQuarterId(date = new Date()) {
    const year = date.getFullYear();
    const monthIndex = date.getMonth(); // 0-based
    const quarter = Math.floor(monthIndex / 3) + 1; // 1-4
    return `${year}-Q${quarter}`;
}

// Cache key for global cards (bump version if schema changes)
const GLOBAL_CARDS_CACHE_KEY = 'bol_global_cards_v1';
// session flag so we refresh exactly once per tab session
const GLOBAL_CARDS_REFRESHED_SESSION_KEY = 'bol_global_cards_refreshed_this_session_v1';

export const UserProvider = ({ children }) => {
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loginTime, setLoginTime] = useState(null);
    const [globalCards, setGlobalCards] = useState({});
    const [todayKey, setTodayKey] = useState(getTodayKey());

    // Keep unsubscribe refs so we can safely clean up listeners on user changes/unmount
    const userUnsubRef = useRef(null);

    // New refs for minimizing writes
    const userIdRef = useRef(null);
    const pendingUpdateRef = useRef({});
    const flushTimeoutRef = useRef(null);

    const hasGlobalCards = useMemo(
        () => Object.keys(globalCards || {}).length > 0,
        [globalCards]
    );

    /**
     * GLOBAL CARDS LOADING POLICY
     * - Always hydrate immediately from localStorage if present (fast startup).
     * - Refresh from Firestore exactly ONCE per tab session (prevents stale cache).
     * - After that one refresh, use state/cache for the rest of the session.
     */
    const loadGlobalCards = useCallback(async () => {
        try {
            // 1) Hydrate from localStorage immediately (fast)
            const cached = (typeof localStorage !== 'undefined')
                ? localStorage.getItem(GLOBAL_CARDS_CACHE_KEY)
                : null;

            if (cached) {
                try {
                    const parsed = JSON.parse(cached);
                    if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
                        setGlobalCards(parsed);
                    }
                } catch (e) {
                    console.warn('[UserContext] Failed to parse cached global cards:', e);
                }
            }

            // 2) Refresh exactly once per tab/session
            const canUseSessionStorage = typeof sessionStorage !== 'undefined';
            const didRefreshThisSession = canUseSessionStorage
                ? sessionStorage.getItem(GLOBAL_CARDS_REFRESHED_SESSION_KEY) === '1'
                : false;

            if (didRefreshThisSession) return;

            // Mark immediately to prevent double-fetch if loadGlobalCards is called twice quickly
            if (canUseSessionStorage) {
                sessionStorage.setItem(GLOBAL_CARDS_REFRESHED_SESSION_KEY, '1');
            }

            // 3) Fetch fresh from Firestore (one-time per session)
            const cardsRef = collection(db, 'cards');
            const snap = await getDocs(cardsRef);
            const cardsData = {};
            snap.forEach((d) => {
                cardsData[d.id] = d.data();
            });

            // Only update state/cache if we got something valid
            if (cardsData && typeof cardsData === 'object' && Object.keys(cardsData).length > 0) {
                setGlobalCards(cardsData);

                if (typeof localStorage !== 'undefined') {
                    localStorage.setItem(GLOBAL_CARDS_CACHE_KEY, JSON.stringify(cardsData));
                }
            } else {
                // If Firestore returned empty, fall back to whatever we already have in state/cache.
                if (!hasGlobalCards) {
                    console.warn('[UserContext] Firestore returned 0 global cards.');
                }
            }
        } catch (e) {
            console.error('[UserContext] Failed to load global cards:', e);
        }
    }, [hasGlobalCards]);

    // Tick once a minute and detect when the calendar day changes (local time)
    useEffect(() => {
        const intervalId = setInterval(() => {
            setTodayKey(prev => {
                const next = getTodayKey();
                return prev === next ? prev : next;
            });
        }, 60 * 1000);

        return () => clearInterval(intervalId);
    }, []);

    // ------------------------------
    // Firestore data validators
    // ------------------------------
    function isValidCampaignProgress(c) {
        if (!c || typeof c !== 'object') return false;
        const w = Number(c.world);
        const s = Number(c.stage);
        return Number.isFinite(w) && w >= 1 && Number.isFinite(s) && s >= 1;
    }

    // ------------------------------
    // Centralized queued updater
    // ------------------------------
    const flushQueuedUpdates = useCallback(async () => {
        const uid = userIdRef.current;
        const pending = pendingUpdateRef.current;

        if (!uid) return;
        if (!pending || Object.keys(pending).length === 0) return;

        // Reset before writing to avoid merging new changes into an in-flight payload
        pendingUpdateRef.current = {};
        if (flushTimeoutRef.current) {
            clearTimeout(flushTimeoutRef.current);
            flushTimeoutRef.current = null;
        }

        try {
            const docRef = doc(db, 'users', uid);
            let payload = { ...pending };

            if ('ownedCards' in payload) {
                payload.ownedCards = stripToInstanceShape(payload.ownedCards);
            }

            payload = sanitizeForFirestore(payload);
            await updateDoc(docRef, payload);
            // No manual setUserData needed; onSnapshot will deliver fresh state.
        } catch (err) {
            console.error("[UserContext] Failed to flush queued Firestore updates:", err);
        }
    }, []);

    const persistUserData = useCallback(
        (updatedFields, { immediate = false } = {}) => {
            if (!userIdRef.current) return;

            // Merge with any already queued updates
            pendingUpdateRef.current = {
                ...pendingUpdateRef.current,
                ...updatedFields,
            };

            if (immediate) {
                flushQueuedUpdates();
                return;
            }

            // Debounce flush – multiple rapid calls collapse into one write
            if (!flushTimeoutRef.current) {
                flushTimeoutRef.current = setTimeout(() => {
                    flushQueuedUpdates();
                }, 800); // tune this window as needed
            }
        },
        [flushQueuedUpdates]
    );

    // ------------------------------
    // Global cards: cache + single refresh per tab session
    // ------------------------------
    useEffect(() => {
        // Kick off loading as soon as the provider mounts
        loadGlobalCards();
    }, [loadGlobalCards]);


    // ------------------------------
    // Auth listener → establish live Firestore listener for user doc
    // ------------------------------
    useEffect(() => {
        const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
            // Tear down any previous listeners when auth state changes
            if (userUnsubRef.current) {
                userUnsubRef.current();
                userUnsubRef.current = null;
            }

            // Reset queued updates when auth changes
            pendingUpdateRef.current = {};
            if (flushTimeoutRef.current) {
                clearTimeout(flushTimeoutRef.current);
                flushTimeoutRef.current = null;
            }

            if (user) {
                userIdRef.current = user.uid;
                setLoginTime(Timestamp.now());

                // 🔥 Ensure global cards are loading as soon as the user logs in
                // (session flag prevents duplicate Firestore fetches in the same tab)
                loadGlobalCards();

                const userRef = doc(db, 'users', user.uid);

                // Ensure the user doc exists (create if missing) before we subscribe
                try {
                    const existing = await getDoc(userRef);
                    if (!existing.exists()) {
                        console.warn("No user document found in Firestore. Creating a new one.");
                        const initialDoc = {
                            email: user.email,
                            coins: 0,
                            trophies: 0,
                            league: getLeagueForTrophies(0).name,
                            ownedCards: [],
                            drawTickets: {
                                basic: 0,
                                rarePlus: 0,
                                highRarePlus: 0,
                                superRarePlus: 0
                            },
                            consumables: { soulDust: 0, arenaTicket: 0 },
                            campaign: { world: 1, stage: 1 },
                            createdAt: Timestamp.now(),
                            lastLoginAt: Timestamp.now(),
                            lastDailyArenaRewardDate: null,
                            lastArenaLeagueRewardQuarter: null,
                            arenaRefreshAt: Timestamp.now(),
                            arenaBeatenOpponents: [],
                        };
                        await setDoc(userRef, initialDoc, { merge: true });
                    }
                } catch (e) {
                    console.error("Failed to ensure Firestore user doc exists:", e);
                }

                // Live subscription to the user doc (keeps userData — including campaign — always fresh)
                userUnsubRef.current = onSnapshot(
                    userRef,
                    async (snap) => {
                        if (!snap.exists()) {
                            // If for some reason it was deleted, recreate the minimal doc
                            try {
                                await setDoc(userRef, {
                                    email: user.email,
                                    coins: 0,
                                    trophies: 0,
                                    league: getLeagueForTrophies(0).name,
                                    ownedCards: [],
                                    drawTickets: {
                                        basic: 20,
                                        rarePlus: 0,
                                        highRarePlus: 0,
                                        superRarePlus: 0
                                    },
                                    consumables: { soulDust: 0, arenaTicket: 0 },
                                    campaign: { world: 1, stage: 1 },
                                    createdAt: Timestamp.now(),
                                    lastLoginAt: Timestamp.now(),
                                    lastDailyArenaRewardDate: null,
                                    lastArenaLeagueRewardQuarter: null,
                                }, { merge: true });
                            } catch (e) {
                                console.error("Failed to recreate missing user doc:", e);
                            }
                            return;
                        }

                        const raw = snap.data() || {};

                        // ---- Patch/normalize on the fly (only when needed) ----

                        // Ensure trophies + league exist and match thresholds
                        const trophies = Number.isFinite(raw.trophies) ? raw.trophies : 0;
                        const desiredLeague = getLeagueForTrophies(trophies).name;
                        const needsLeaguePatch = raw.trophies === undefined || raw.league !== desiredLeague;

                        // One-time normalization: ensure evolutionStage is 1-based on login/snapshot
                        const originalOwned = Array.isArray(raw.ownedCards) ? raw.ownedCards : [];
                        const patchedOwned = originalOwned.map(c => ({
                            ...c,
                            evolutionStage: Math.max(1, Number(c?.evolutionStage ?? 1)),
                        }));
                        const ownedChanged =
                            JSON.stringify(stripToInstanceShape(patchedOwned)) !==
                            JSON.stringify(stripToInstanceShape(originalOwned));

                        // Ensure campaign progress exists & is valid (object with {world, stage})
                        let campaignProgress = raw.campaign;
                        const needsCampaignPatch = !isValidCampaignProgress(campaignProgress);
                        if (needsCampaignPatch) {
                            campaignProgress = { world: 1, stage: 1 };
                        }

                        // Batch any necessary patches
                        if (needsLeaguePatch || ownedChanged || needsCampaignPatch) {
                            const toWrite = {};
                            if (needsLeaguePatch) {
                                toWrite.trophies = trophies;
                                toWrite.league = desiredLeague;
                            }
                            if (ownedChanged) {
                                toWrite.ownedCards = stripToInstanceShape(patchedOwned);
                            }
                            if (needsCampaignPatch) {
                                toWrite.campaign = campaignProgress;
                            }

                            try {
                                await updateDoc(userRef, sanitizeForFirestore(toWrite));
                            } catch (e) {
                                console.error("Failed to patch user doc on snapshot:", e);
                            }
                        }

                        // Compose the userData shape for the app
                        const userDoc = {
                            uid: user.uid,
                            email: user.email,
                            ...raw,
                            // Make sure patched fields are reflected locally right now
                            trophies,
                            league: desiredLeague,
                            ownedCards: ownedChanged ? patchedOwned : originalOwned,
                            campaign: needsCampaignPatch ? campaignProgress : raw.campaign
                        };

                        setUserData(userDoc);
                        setLoading(false);
                    },
                    (err) => {
                        console.error("[UserContext] onSnapshot error:", err);
                        setLoading(false);
                    }
                );
            } else {
                // Logged out: clean up state
                userIdRef.current = null;
                pendingUpdateRef.current = {};
                if (flushTimeoutRef.current) {
                    clearTimeout(flushTimeoutRef.current);
                    flushTimeoutRef.current = null;
                }

                if (userUnsubRef.current) {
                    userUnsubRef.current();
                    userUnsubRef.current = null;
                }
                setUserData(null);
                setGlobalCards({});
                setLoading(false);
            }
        });

        return () => {
            // Cleanup on unmount
            if (userUnsubRef.current) {
                userUnsubRef.current();
                userUnsubRef.current = null;
            }
            if (flushTimeoutRef.current) {
                clearTimeout(flushTimeoutRef.current);
                flushTimeoutRef.current = null;
            }
            unsubscribeAuth();
        };
        // ⬇️ include loadGlobalCards so we can call it inside
    }, [loadGlobalCards]);


    // ------------------------------
    // Local helpers (optimistic UI; source of truth is Firestore via onSnapshot)
    // ------------------------------
    const updateCardActivity = (instanceId, isActive) => {
        if (!userData) return;
        const updatedCards = (userData.ownedCards || []).map(c =>
            c.instanceId === instanceId ? { ...c, active: !!isActive } : c
        );
        setUserData(prev => ({ ...prev, ownedCards: updatedCards }));
        persistUserData({ ownedCards: updatedCards });
    };

    const updateDefenseActivity = (instanceId, isDefenseActive) => {
        if (!userData) return;
        const updatedCards = (userData.ownedCards || []).map(c =>
            c.instanceId === instanceId ? { ...c, defenseActive: !!isDefenseActive } : c
        );
        setUserData(prev => ({ ...prev, ownedCards: updatedCards }));
        persistUserData({ ownedCards: updatedCards });
    };

    const updateActiveOrder = (instanceId, activeOrder) => {
        if (!userData) return;
        const updatedCards = (userData.ownedCards || []).map(c =>
            c.instanceId === instanceId ? { ...c, activeOrder } : c
        );
        setUserData(prev => ({ ...prev, ownedCards: updatedCards }));
        persistUserData({ ownedCards: updatedCards });
    };

    const updateDefenseOrder = (instanceId, defenseOrder) => {
        if (!userData) return;
        const updatedCards = (userData.ownedCards || []).map(c =>
            c.instanceId === instanceId ? { ...c, defenseOrder } : c
        );
        setUserData(prev => ({ ...prev, ownedCards: updatedCards }));
        persistUserData({ ownedCards: updatedCards });
    };

    const addCardsToOwned = (newCards) => {
        if (!userData || !Array.isArray(newCards)) return;

        const formattedCards = newCards.map(card => ({
            instanceId: uuid(),
            cardId: card.id || card.cardId,
            level: 1,
            xp: 0,
            evolutionStage: 1, // New cards always start at stage 1
            active: false,
            defenseActive: false,
        }));

        const updatedCards = [...(userData.ownedCards || []), ...formattedCards];
        setUserData(prev => ({ ...prev, ownedCards: updatedCards }));
        persistUserData({ ownedCards: updatedCards });
    };

    const getXpFromCard = (card) => {
        const rarityMultiplier = rarityXpMultiplier[card.rarity] || 0;
        return rarityMultiplier * card.level;
    };

    const levelUpCard = (targetInstanceId, sacrificedInstanceIds = []) => {
        if (!userData || !Array.isArray(sacrificedInstanceIds)) return;

        const updatedCards = [...(userData.ownedCards || [])];
        const targetIndex = updatedCards.findIndex(c => c.instanceId === targetInstanceId);
        const sacrificed = updatedCards.filter(c => sacrificedInstanceIds.includes(c.instanceId));

        if (targetIndex === -1 || sacrificed.length === 0) return;

        const targetCard = { ...updatedCards[targetIndex] };
        const baseCardData = globalCards[targetCard.cardId];
        const maxLevel = getMaxLevel(baseCardData?.rarity || 'Common');

        let totalXp = sacrificed.reduce((sum, c) => {
            const base = globalCards[c.cardId];
            return sum + getXpFromCard({ ...base, ...c });
        }, 0);

        let currentXp = targetCard.xp || 0;
        let currentLevel = targetCard.level || 1;

        currentXp += totalXp;

        while (currentLevel < maxLevel) {
            const requiredXp = getXpForLevel(currentLevel);
            if (currentXp >= requiredXp) {
                currentXp -= requiredXp;
                currentLevel++;
            } else {
                break;
            }
        }

        targetCard.level = currentLevel;
        targetCard.xp = currentXp;

        const remainingCards = updatedCards.filter(c => !sacrificedInstanceIds.includes(c.instanceId));
        const newList = [...remainingCards];
        const newIdx = newList.findIndex(c => c.instanceId === targetInstanceId);
        if (newIdx >= 0) newList[newIdx] = targetCard;
        else newList.push(targetCard);

        setUserData(prev => ({ ...prev, ownedCards: newList }));
        persistUserData({ ownedCards: newList });
    };

    const ascendCard = async (targetInstanceId, { cost } = {}) => {
        // Use the functional form to avoid stale userData
        let payloadForPersist = null;

        setUserData(prev => {
            if (!prev) return prev;

            const owned = Array.isArray(prev.ownedCards) ? [...prev.ownedCards] : [];
            const idx = owned.findIndex(c => c.instanceId === targetInstanceId);
            if (idx === -1) return prev;

            const inst = { ...owned[idx] };

            // Get rarity from global card data (instance typically doesn't carry rarity)
            const base = globalCards?.[inst.cardId];
            if (!base) return prev;

            const rarity = base.rarity || 'Common';
            const baseMax = getMaxLevel(rarity);

            // Default ascensions to 0 if missing
            const ascensions = Number(inst.ascensions ?? 0);

            // Current cap BEFORE this ascension
            const currentCap = baseMax + ascensions * 10;

            // Must be at/above current cap AND at max evolution (3)
            const isAtCap = Number(inst.level || 1) >= currentCap;
            const isMaxEvo = Number(inst.evolutionStage || 0) === 3;
            if (!isAtCap || !isMaxEvo) return prev;

            // Compute cost (prefer param if supplied)
            const requiredCost = typeof cost === 'number' ? cost : getAscensionCost(rarity, ascensions);
            const soulDust = Number(prev?.consumables?.soulDust ?? 0);
            if (soulDust < requiredCost) return prev; // not enough SD

            // Apply ascension
            const newAscensions = ascensions + 1;
            const newCap = baseMax + newAscensions * 10;

            // Raise level by +10 but never exceed the NEW cap
            const newLevel = Math.min(Number(inst.level || 1) + 10, newCap);

            const updatedInst = {
                ...inst,
                ascensions: newAscensions, // ensure we write this on first ascend
                level: newLevel,
                // xp policy: keep as-is; set xp: 0 here if you want to reset on ascend
            };

            const updatedOwned = [...owned];
            updatedOwned[idx] = updatedInst;

            const soulDustCurrent = Number(prev?.consumables?.soulDust ?? 0);
            const newSoulDust = soulDustCurrent - requiredCost;
            const newConsumables = { ...(prev.consumables || {}), soulDust: newSoulDust };

            // prepare payload for persistUserData outside setState
            payloadForPersist = { consumables: newConsumables, ownedCards: updatedOwned };

            return { ...prev, consumables: newConsumables, ownedCards: updatedOwned };
        });

        // Persist AFTER state update (best effort)
        try {
            if (payloadForPersist) {
                persistUserData(payloadForPersist);
            }
        } catch (e) {
            console.error('persistUserData (ascendCard) failed:', e);
        }
    };

    // Spend stored XP into levels for a single card
    const _applyXpToLevels = (card) => {
        const base = globalCards[card.cardId];
        if (!base) return card; // no catalog entry yet

        const maxLevel = getMaxLevel(base?.rarity || 'Common');

        let level = Number(card.level ?? 1);
        let xp = Number(card.xp ?? 0);
        let changed = false;

        // Spend XP while we can
        while (level < maxLevel) {
            const required = getXpForLevel(level); // XP required to go from level -> level+1
            if (!Number.isFinite(required) || required <= 0) break;
            if (xp >= required) {
                xp -= required;
                level += 1;
                changed = true;
            } else {
                break;
            }
        }

        // If capped, clear leftover XP (optional policy)
        if (level >= maxLevel && xp !== 0) {
            xp = 0;
            changed = true;
        }

        return changed ? { ...card, level, xp } : card;
    };

    // Sweep all cards; persist only if something actually changed
    const sweepPassiveLevelUps = useCallback(() => {
        if (!userData?.ownedCards || !hasGlobalCards) return;

        const original = userData.ownedCards;
        const updated = original.map(_applyXpToLevels);

        const changed =
            updated.length !== original.length ||
            JSON.stringify(stripToInstanceShape(updated)) !== JSON.stringify(stripToInstanceShape(original));

        if (changed) {
            // Update local state immediately for snappy UI
            setUserData(prev => ({ ...prev, ownedCards: updated }));
            // Persist normalized shape; onSnapshot will keep us authoritative
            persistUserData({ ownedCards: updated });
        }
    }, [userData?.ownedCards, hasGlobalCards, persistUserData, globalCards]);

    const evolveCard = (targetInstanceId, sacrificeInstanceId) => {
        if (!userData || !globalCards) return;

        const updatedCards = [...(userData.ownedCards || [])];

        const targetIndex = updatedCards.findIndex(c => c.instanceId === targetInstanceId);
        const sacrificeIndex = updatedCards.findIndex(c => c.instanceId === sacrificeInstanceId);

        if (targetIndex === -1 || sacrificeIndex === -1) return;

        const target = { ...updatedCards[targetIndex] };
        const sacrifice = updatedCards[sacrificeIndex];

        if (target.cardId !== sacrifice.cardId) {
            alert("Cards must be the same type to evolve.");
            return;
        }

        // Compare using a 1-based default for evolution stage
        if ((target.evolutionStage ?? 1) !== (sacrifice.evolutionStage ?? 1)) {
            alert("Both cards must have the same evolution stage.");
            return;
        }

        const evolvedStatsBoost = Math.floor((sacrifice.level || 1) * 0.5);

        // Increment from a 1-based baseline
        target.evolutionStage = (target.evolutionStage ?? 1) + 1;
        target.level = 1;
        target.xp = 0;
        target.evolutionBonus = (target.evolutionBonus || 0) + evolvedStatsBoost;

        // Remove the sacrificed card
        updatedCards.splice(sacrificeIndex, 1);

        // Put back the evolved target
        const patched = updatedCards.map(c => c.instanceId === target.instanceId ? target : c);

        setUserData(prev => ({ ...prev, ownedCards: patched }));
        persistUserData({ ownedCards: patched });
    };

    const spendCoins = (amount) => {
        if (!userData || (userData.coins ?? 0) < amount) return false;
        const newAmount = (userData.coins ?? 0) - amount;
        setUserData((prev) => ({ ...prev, coins: newAmount }));
        // Coins are important – flush immediately for safety
        persistUserData({ coins: newAmount }, { immediate: true });
        return true;
    };

    const addCoins = (amount) => {
        if (!userData) return;
        setUserData((prev) => {
            const newAmount = (prev.coins ?? 0) + amount;
            // Flush coin changes immediately as well
            persistUserData({ coins: newAmount }, { immediate: true });
            return { ...prev, coins: newAmount };
        });
    };

    const addSoulDust = (amount) => {
        if (!userData || !Number.isFinite(amount) || amount <= 0) return;
        setUserData(prev => {
            const current = Number(prev?.consumables?.soulDust ?? 0);
            const newVal = current + Math.floor(amount);
            const newConsumables = { ...(prev.consumables || {}), soulDust: newVal };
            // Persist
            persistUserData({ consumables: newConsumables });
            return { ...prev, consumables: newConsumables };
        });
    };

    const spendSoulDust = (amount) => {
        if (!userData || !Number.isFinite(amount) || amount <= 0) return false;
        const current = Number(userData?.consumables?.soulDust ?? 0);
        if (current < amount) return false;
        const newVal = current - Math.floor(amount);
        const newConsumables = { ...(userData.consumables || {}), soulDust: newVal };
        setUserData(prev => ({ ...prev, consumables: newConsumables }));
        persistUserData({ consumables: newConsumables });
        return true;
    };

    const addTrophies = (amount) => {
        if (!userData || !Number.isFinite(amount)) return;
        setUserData((prev) => {
            const current = prev.trophies || 0;
            const nextT = current + Math.max(0, Math.floor(amount));
            const nextLeague = getLeagueForTrophies(nextT).name;
            persistUserData({ trophies: nextT, league: nextLeague });
            return { ...prev, trophies: nextT, league: nextLeague };
        });
    };

    const removeTrophies = (amount) => {
        if (!userData || !Number.isFinite(amount)) return;
        setUserData((prev) => {
            const current = prev.trophies || 0;
            const nextT = Math.max(0, current - Math.max(0, Math.floor(amount)));
            const nextLeague = getLeagueForTrophies(nextT).name;
            persistUserData({ trophies: nextT, league: nextLeague });
            return { ...prev, trophies: nextT, league: nextLeague };
        });
    };

    const hasDrawTicket = (ticketType) => {
        return (userData?.drawTickets?.[ticketType] ?? 0) > 0;
    };

    const consumeDrawTicket = (ticketType) => {
        setUserData((prev) => {
            if (!prev) return prev;
            const current = prev.drawTickets?.[ticketType] ?? 0;
            if (current <= 0) return prev;

            const updatedDrawTickets = {
                ...(prev.drawTickets || {}),
                [ticketType]: current - 1,
            };

            const updatedUser = { ...prev, drawTickets: updatedDrawTickets };
            // Persist only the changed field
            persistUserData({ drawTickets: updatedDrawTickets });
            return updatedUser;
        });
    };

    const addDrawTickets = (ticketType, amount = 1) => {
        setUserData((prev) => {
            if (!prev) return prev;

            const current = prev.drawTickets?.[ticketType] ?? 0;
            const updatedDrawTickets = {
                ...(prev.drawTickets || {}),
                [ticketType]: current + amount,
            };

            const updatedUser = { ...prev, drawTickets: updatedDrawTickets };
            // Persist only the changed field
            persistUserData({ drawTickets: updatedDrawTickets });
            return updatedUser;
        });
    };

    const hasBasicTicket = () => hasDrawTicket('basic');
    const consumeBasicTicket = () => consumeDrawTicket('basic');

    // ------------------------------
    // Arena daily + quarterly rewards
    //
    // - Daily: once per calendar day (local) based on current league.
    // - Quarterly: once per quarter (local) based on *current* league, then trophies reset to 0,
    //   and league reset to the base league (0 trophies).
    //
    // Reward tracking fields stored in user doc:
    //   - lastDailyArenaRewardDate: "YYYY-MM-DD" (local)
    //   - lastArenaLeagueRewardQuarter: "YYYY-Q{1-4}" (local)
    // ------------------------------
    useEffect(() => {
        if (!userData) return;

        const runArenaRewards = async () => {
            const today = todayKey;
            const currentQuarterId = getCurrentQuarterId();

            const trophies = Number(userData.trophies || 0);
            const leagueCfg = getLeagueForTrophies(trophies);
            const dailyCfg = leagueCfg.dailyRewards || null;
            const quarterlyCfg = leagueCfg.leagueRewards || null;

            let updatedConsumables = { ...(userData.consumables || {}) };
            let updatedTickets = { ...(userData.drawTickets || {}) };
            let updatedTrophies = trophies;
            let updatedLeagueName = userData.league || leagueCfg.name;
            let changed = false;

            const updates = {};

            // Daily reward: one per day, per user
            if (dailyCfg && userData.lastDailyArenaRewardDate !== today) {
                const sdAdd = Number(dailyCfg.soulDust || 0);
                if (sdAdd > 0) {
                    const currentSd = Number(updatedConsumables.soulDust ?? 0);
                    updatedConsumables.soulDust = currentSd + sdAdd;
                }

                if (dailyCfg.tickets && typeof dailyCfg.tickets === 'object') {
                    const basicAdd = Number(dailyCfg.tickets.basic || 0);
                    if (basicAdd > 0) {
                        const currentBasic = Number(updatedTickets.basic ?? 0);
                        updatedTickets.basic = currentBasic + basicAdd;
                    }
                }

                updates.lastDailyArenaRewardDate = today;
                changed = true;
            }

            // Quarterly rewards + trophy reset:
            // Only if:
            //   - we have a quarterly config
            //   - it's a new quarter compared to lastArenaLeagueRewardQuarter
            //   - and user actually has trophies (>0) so we don't spam brand new accounts
            const lastQuarterId = userData.lastArenaLeagueRewardQuarter || null;
            if (
                quarterlyCfg &&
                lastQuarterId !== currentQuarterId &&
                trophies > 0
            ) {
                const sdAdd = Number(quarterlyCfg.soulDust || 0);
                if (sdAdd > 0) {
                    const currentSd = Number(updatedConsumables.soulDust ?? 0);
                    updatedConsumables.soulDust = currentSd + sdAdd;
                }

                if (quarterlyCfg.tickets && typeof quarterlyCfg.tickets === 'object') {
                    const basicAdd = Number(quarterlyCfg.tickets.basic || 0);
                    if (basicAdd > 0) {
                        const currentBasic = Number(updatedTickets.basic ?? 0);
                        updatedTickets.basic = currentBasic + basicAdd;
                    }
                }

                // Reset trophies + league at end of quarter
                updatedTrophies = 0;
                const baseLeague = getLeagueForTrophies(0);
                updatedLeagueName = baseLeague.name;

                updates.lastArenaLeagueRewardQuarter = currentQuarterId;
                updates.trophies = updatedTrophies;
                updates.league = updatedLeagueName;
                changed = true;
            }

            if (!changed) return;

            updates.consumables = updatedConsumables;
            updates.drawTickets = updatedTickets;

            try {
                persistUserData(updates);
            } catch (err) {
                console.error('[Arena] Failed to persist arena rewards:', err);
            }
        };

        runArenaRewards();
    }, [userData, todayKey, persistUserData]);

    useEffect(() => {
        sweepPassiveLevelUps();
    }, [sweepPassiveLevelUps]);

    return (
        <UserContext.Provider
            value={{
                userData,
                setUserData,
                persistUserData,
                updateCardActivity,
                updateDefenseActivity,
                updateActiveOrder,
                updateDefenseOrder,
                addCardsToOwned,
                spendCoins,
                addCoins,
                addTrophies,
                removeTrophies,
                consumeBasicTicket,
                hasBasicTicket,
                hasDrawTicket,
                consumeDrawTicket,
                addDrawTickets,
                globalCards,
                levelUpCard,
                ascendCard,
                loading,
                loginTime,
                evolveCard,
                addSoulDust,
                spendSoulDust,
            }}
        >
            {children}
        </UserContext.Provider>
    );
};
